import { useEffect, useState } from 'react'

/**
 * AF-126 — SLA timer (drives both queue cell + detail banner).
 *
 * Returns the formatted label, a tone token (used to pick text/badge color),
 * and a 0-100 progress percent. Re-renders once per minute (no `setInterval`
 * — chains a single setTimeout to the next minute boundary so 1000 SLA cells
 * don't all wake up at the same wall-clock second).
 */

export type SlaTone = 'ok' | 'warn' | 'critical' | 'breached' | 'none'

export interface SlaState {
  /** Display label, e.g. "47m left" or "Breached · 4h ago". */
  label: string
  tone: SlaTone
  /** 0-100 elapsed/total. Capped at 100 — the bar uses a stripe pattern past breach. */
  progress: number
  /** True when the deadline has passed. */
  breached: boolean
}

interface Args {
  startedAt: string
  deadlineAt: string
  /** When false, returns `tone='none'` and a static "—" label. */
  active?: boolean
  /** Override clock for tests / Storybook. */
  now?: number
}

export function useSlaTimer({ startedAt, deadlineAt, active = true, now }: Args): SlaState {
  // Track current time in state so we never call Date.now() during render
  // (the `react-hooks/purity` lint rule forbids that). The effect below
  // schedules a refresh on the next minute boundary; tests pin via `now`.
  const [nowMs, setNowMs] = useState<number>(() => now ?? Date.now())

  useEffect(() => {
    if (!active || now !== undefined) return
    // Schedule the next refresh at the next wall-clock minute boundary.
    // setState only happens inside the timer callback so the
    // `set-state-in-effect` lint rule is satisfied.
    const msToNextMinute = 60_000 - (Date.now() % 60_000)
    const id = window.setTimeout(() => setNowMs(Date.now()), msToNextMinute)
    return () => window.clearTimeout(id)
  }, [active, now, nowMs])

  if (!active) {
    return { label: '—', tone: 'none', progress: 0, breached: false }
  }

  const t = now ?? nowMs
  const start = new Date(startedAt).getTime()
  const deadline = new Date(deadlineAt).getTime()
  const total = Math.max(1, deadline - start)
  const remaining = deadline - t
  const elapsed = t - start

  if (remaining <= 0) {
    const breachedFor = -remaining
    return {
      label: `Breached · ${formatDuration(breachedFor, { compact: true })} ago`,
      tone: 'breached',
      progress: Math.min(100, (elapsed / total) * 100),
      breached: true,
    }
  }

  const tone = pickTone(remaining)
  return {
    label: `${formatDuration(remaining, { compact: true })} left`,
    tone,
    progress: (elapsed / total) * 100,
    breached: false,
  }
}

function pickTone(remainingMs: number): SlaTone {
  const minutes = remainingMs / 60_000
  if (minutes <= 30) return 'critical'
  if (minutes <= 60) return 'warn'
  if (minutes <= 120) return 'ok'
  return 'ok'
}

interface FormatOpts {
  compact?: boolean
}

/** "1h 23m" / "23m" / "<1m" / "2d 4h". */
export function formatDuration(ms: number, opts: FormatOpts = {}): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000))
  if (totalMinutes < 1) return '<1m'
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) {
    return opts.compact ? `${days}d ${hours}h` : `${days}d ${hours}h ${minutes}m`
  }
  if (hours > 0) {
    return opts.compact ? `${hours}h ${minutes}m` : `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

/**
 * Tailwind text color class for an SLA tone. Centralised here so cell + banner
 * + detail header stay in sync.
 */
export function slaToneTextClass(tone: SlaTone): string {
  switch (tone) {
    case 'ok':
      return 'text-status-success'
    case 'warn':
      return 'text-status-pending'
    case 'critical':
      return 'text-status-failed'
    case 'breached':
      return 'text-status-failed font-medium'
    case 'none':
      return 'text-muted-fg/50'
  }
}

/** Tailwind background color class for an SLA tone (used by the banner bar). */
export function slaToneBgClass(tone: SlaTone): string {
  switch (tone) {
    case 'ok':
      return 'bg-status-success'
    case 'warn':
      return 'bg-status-pending'
    case 'critical':
    case 'breached':
      return 'bg-status-failed'
    case 'none':
      return 'bg-muted'
  }
}
